"use client";

import React, { useMemo, useState, useEffect } from "react";
import dynamic from "next/dynamic";
import clsx from "clsx";
import { isFilled } from "@prismicio/helpers";
import { Settings } from "@/app/lib/settings";
import { storyMatchesType } from '@/app/lib/storyTypeUtils';
import { ConditionalLink } from "@/app/lib/ConditionalLink";
import { PrismicNextImage } from "@prismicio/next";
import { RichText } from "@/app/lib/RichText";
import { RevealOnScroll } from '@/app/lib/RevealOnScroll';
import Chevron from "@/app/assets/images/icon_chevron.svg";
import styles from "./storiesGrid.module.scss";
import sharedStyles from "@/app/shared.module.scss";
import { useRouter, usePathname } from "next/navigation";

export function StoriesGrid({ slice, stories = [] }) {
	const router = useRouter();
	const pathname = usePathname();

	const [selectedFilter, setSelectedFilter] = useState('');
	const [isMounted, setIsMounted] = useState(false);

	const Masonry = dynamic(
		() => import('masonic').then((mod) => mod.Masonry),
		{ ssr: false },
	);

	const keyToSlug = (key) => key.toLowerCase().replace(/ /g, '-');

	const slugToKey = (slug) => {
		for (const key of Object.keys(Settings.storyTypes)) {
			if (keyToSlug(key) === slug) {
				return key;
			}
		}
		return '';
	};

	useEffect(() => {

		if (typeof window === 'undefined') {
			setIsMounted(true);
			return;
		}

		const segments = pathname.split('/').filter(Boolean);
		let filterKey = '';

		if (segments.length > 1) {
			const lastSegment = segments[segments.length - 1];
			filterKey = slugToKey(lastSegment);
		}

		setSelectedFilter(filterKey || 'All');

		setIsMounted(true);
	}, [pathname]);

	const toggleFilter = (filterKey) => {
		const slug = keyToSlug(filterKey);
		const segments = pathname.split('/').filter(Boolean);

		const basePath = `/${segments[0]}`;
		const isTogglingOff = filterKey === 'All' || selectedFilter === filterKey;

		let newPath = basePath;

		if (!isTogglingOff) {
			newPath = `${basePath}/${slug}`;
		}

		router.replace(newPath, { shallow: true });

	};


	const filteredStories = useMemo(() => {
		if (!isMounted) return stories;
		if (!selectedFilter || selectedFilter === 'All') return stories;

		return stories.filter((story) => storyMatchesType(story, selectedFilter));
	}, [stories, selectedFilter, isMounted]);

	const StoryCard = ({ data: story, index, width }) => {
		const targetUrl = isFilled.link(story?.data?.external_url)
			? story?.data?.external_url
			: story?.data?.slices?.length
				? `/${Settings.storiesPrefix}/${story?.uid}`
				: null;

		return (
			<RevealOnScroll scrollDelayRem={(index % 3) * 5} key={story?.uid ?? index}>
				<ConditionalLink
					condition={story?.data?.slices?.length || isFilled.link(story?.data?.external_url)}
					url={targetUrl}
					target={isFilled.link(story?.data?.external_url) ? "_blank" : "_self"}
					className={clsx(styles.story)}
				>
					{isFilled.image(story?.data?.image) && (
						<div className={clsx(styles.image_wrapper)}>
							<PrismicNextImage
								field={story?.data?.image}
								fallbackAlt=""
								className={clsx(styles.image)}
							/>
						</div>
					)}
					{isFilled.select(story?.data?.type) && (
						<p className={clsx(styles.type)}>{story?.data?.type}</p>
					)}
					{isFilled.keyText(story?.data?.title) && (
						<h3 className={clsx(styles.story_title)}>{story?.data?.title}</h3>
					)}
					{isFilled.richText(story?.data?.excerpt) && (
						<div className={styles.introduction}>
							<RichText field={story?.data?.excerpt} />
						</div>
					)}

				</ConditionalLink>
			</RevealOnScroll>
		);
	};

	return (
		<div className={clsx(styles.wrapper)}>

			{/* Filters */}
			{slice?.primary?.show_filter && (
				<div className={clsx(sharedStyles.filters)}>
					<div className={clsx(sharedStyles.label_wrap)}>
						<div className={clsx(sharedStyles.label)}>Filter</div>
						<Chevron className={sharedStyles.chevron} />
					</div>

					{Object.entries(Settings.storyTypes).map(([key, value]) => {
						const isHighlighted = selectedFilter === key;
						return (
							<span
								key={key}
								onClick={() => toggleFilter(key)}
								className={clsx(sharedStyles.item, {
									[sharedStyles.highlighted]: isHighlighted,
								})}
							>
								{value}
							</span>
						);
					})}
				</div>
			)}

			{/* Grid */}
			{isMounted ? (
				filteredStories.length ? (
					<div className={clsx(styles.main)}>
						<Masonry
							// Data
							items={filteredStories}
							itemKey={(data) => data?.uid ?? data?.id ?? Math.random()}
							render={StoryCard}
							columnWidth={360}
							maxColumnCount={3}
							columnGutter={30}
							rowGutter={30}
							className={styles.masonry}
							overscanBy={2}
						/>
					</div>
				) : (
					<div className={styles.noResults}>
						No stories found for the selected filter.
					</div>
				)
			) : null}
		</div>
	);
}